import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BillSummary from "@/components/billing/BillSummary";
import BillItems from "@/components/billing/BillItems";
import { adminApi } from "@/lib/api";
import type { AdminTeamSummary, TeamBill } from "@/lib/types";
import { useLanguage } from "@/contexts/LanguageContext";

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full mx-4 sci-card p-6 max-h-[90vh] overflow-y-auto max-w-[560px]">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-white" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
            {title}
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white">
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// PreviewBillModal calls the read-only GET .../bills:preview endpoint to show
// what a bill for an arbitrary [period_start, period_end) window would look
// like — same computation as the real out-billing flow (discount applied,
// history-aware voucher/cash offsets), but never persisted. The result is
// rendered with the same BillSummary/BillItems used for real bills, with a
// prominent "preview only" notice above it. Closing the modal discards the
// result; there is no save/generate action here.
export default function PreviewBillModal({
  teams,
  initialTeamId,
  onClose,
}: {
  teams: AdminTeamSummary[];
  initialTeamId?: string;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const [teamId, setTeamId] = useState(initialTeamId || "");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<TeamBill | null>(null);

  const submit = async () => {
    if (!teamId || !periodStart || !periodEnd) {
      toast.error(t("admin.bills.previewRequiredError"));
      return;
    }
    if (periodStart > periodEnd) {
      toast.error(t("admin.bills.previewRangeError"));
      return;
    }
    setBusy(true);
    try {
      const bill = await adminApi.previewBill(teamId, {
        period_start: periodStart,
        period_end: periodEnd,
      });
      setResult(bill);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={t("admin.bills.previewTitle")} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-slate-400 mb-1.5 block">{t("admin.bills.colTeam")}</Label>
          <select
            value={teamId}
            onChange={(e) => {
              setTeamId(e.target.value);
              setResult(null);
            }}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white h-9 focus:outline-none focus:border-blue-500/50"
          >
            <option value="">{t("admin.bills.previewSelectTeam")}</option>
            {teams.map((ts) => (
              <option key={ts.team.id} value={ts.team.id}>
                {ts.team.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-400 mb-1.5 block">{t("admin.bills.previewStart")}</Label>
            <Input
              type="date"
              value={periodStart}
              onChange={(e) => {
                setPeriodStart(e.target.value);
                setResult(null);
              }}
              className="bg-white/5 border-white/10 text-white text-sm"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-400 mb-1.5 block">{t("admin.bills.previewEnd")}</Label>
            <Input
              type="date"
              value={periodEnd}
              onChange={(e) => {
                setPeriodEnd(e.target.value);
                setResult(null);
              }}
              className="bg-white/5 border-white/10 text-white text-sm"
            />
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-5">
        <Button variant="ghost" onClick={onClose} className="flex-1 text-slate-400 hover:text-white border border-white/10">
          {t("admin.common.cancel")}
        </Button>
        <Button onClick={submit} disabled={busy} className="flex-1 btn-primary-sci">
          {busy ? "..." : t("admin.bills.previewSubmit")}
        </Button>
      </div>

      {result && (
        <div className="mt-5 pt-5 border-t border-white/10 space-y-4">
          <div className="text-center text-[11px] font-semibold tracking-wide text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg py-1.5">
            {t("admin.bills.previewNotice")}
          </div>
          <BillSummary bill={result} />
          <div>
            <div className="text-[10px] text-slate-600 uppercase tracking-wider px-2 pb-1">
              {t("admin.bills.lineItems")}
            </div>
            <BillItems items={result.items || []} />
          </div>
        </div>
      )}
    </ModalShell>
  );
}
